// Persona-specific welcome messages for the Studio Chat

import { RecipeFormData } from '@/types/recipe';

/**
 * Persona welcome messages - each chef has a unique voice.
 *
 * The chat header now displays the chef's name + tagline, so the welcome
 * message body drops the roleplay prefix (e.g. "*The X Chef bows*") that
 * used to introduce the persona. Keeps the emoji opener + first line about
 * the recipe, then 1-2 lines of what the chef can help with.
 */
const PERSONA_WELCOMES: Record<string, (title: string) => string> = {
    'TestKitchenScientist': (title: string) =>
        `🔬 "${title}" has been rigorously tested and optimized for success.

I can explain the science behind any step, suggest evidence-based alternatives, or answer your cooking questions. What would you like to know?`,

    'TheCulinaryAlchemist': (title: string) =>
        `✨ Ah, "${title}" — a transformation complete.

Ask me about techniques, ingredient substitutions, or how to take this dish even further. What shall we explore?`,

    'TheHeritageChef': (title: string) =>
        `🙏 "${title}" honors the traditions passed down through generations.

I can share the cultural wisdom behind this dish, explain authentic techniques, or suggest adaptations while respecting the soul of the recipe. How may I guide you?`,

    'TheEssentialistChef': (title: string) =>
        `🍃 "${title}" embodies the art of restraint — every element serves a purpose.

I'm here to discuss the philosophy of simplicity, ingredient sourcing, or the 'why' behind each deliberate choice. What calls your attention?`,

    'TheElementalChef': (title: string) =>
        `🔥 "${title}" celebrates the fundamentals.

I can explain the core techniques at play, discuss how seasonal ingredients might vary the dish, or help you master the foundations. Where shall we begin?`,

    'TheVitalityChef': (title: string) =>
        `💚 "${title}" is crafted for nourishment without sacrificing satisfaction.

I can explain the nutritional benefits of each choice, suggest healthy adaptations, or discuss smart ingredient swaps. What's on your mind?`
};

/**
 * Default welcome message when persona is not recognized
 */
function getDefaultWelcome(title: string): string {
    return `👨‍🍳 Your recipe "${title}" is ready!

I can answer questions about ingredients, techniques, or cooking tips. If you'd like to make changes to the recipe, just let me know what you'd like to modify.

How can I help?`;
}

/**
 * Get the welcome message for a specific persona
 */
export function getPersonaWelcome(personaId: string, recipe: RecipeFormData): string {
    const title = recipe.title || 'Untitled Recipe';
    const welcomeFn = PERSONA_WELCOMES[personaId];

    if (welcomeFn) {
        return welcomeFn(title);
    }

    return getDefaultWelcome(title);
}

/**
 * Get a short persona introduction (for UI display)
 */
export function getPersonaIntro(personaId: string): string {
    const intros: Record<string, string> = {
        'TestKitchenScientist': 'Your evidence-based cooking guide',
        'TheCulinaryAlchemist': 'Master of culinary transformation',
        'TheHeritageChef': 'Guardian of authentic traditions',
        'TheEssentialistChef': 'Philosopher of pure flavors',
        'TheElementalChef': 'Teacher of fundamental techniques',
        'TheVitalityChef': 'Champion of healthy delicious cooking'
    };

    return intros[personaId] || 'Your AI cooking assistant';
}
